import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { validatePassword } from "../utils/passwordValidator";

const USER_API = process.env.REACT_APP_USER_SERVICE_URL;
const BASE_API = USER_API + "/users";

const Register = () => {
  const navigate = useNavigate();

  const [darkMode, setDarkMode] = useState(true);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // success | error
  const [agreed, setAgreed] = useState(false);

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const otpInputs = useRef([]);

  const [pendingRegistrationId, setPendingRegistrationId] = useState(null);

  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    const saved = localStorage.getItem("darkMode");
    if (saved) setDarkMode(saved === "true");
  }, []);

  useEffect(() => {
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode]);

  const [showPassword, setShowPassword] = useState(false);

  // Auto-clear messages
  useEffect(() => {
    if (message) {
      const t = setTimeout(() => setMessage(""), 3500);
      return () => clearTimeout(t);
    }
  }, [message]);

  // OTP resend timer
  useEffect(() => {
    if (step === 2 && resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
    if (resendTimer === 0) setCanResend(true);
  }, [resendTimer, step]);

  const showSuccess = (msg) => {
    setMessage(msg);
    setMessageType("success");
  };

  const showError = (msg) => {
    setMessage(msg);
    setMessageType("error");
  };

  const handlePhoneChange = (value) => {
    value = value.replace(/\D/g, "");
    value = value.replace(/^251/, "");
    value = "+251" + value;

    if (value.length > 12) value = value.substring(0, 12);
    setFormData({ ...formData, phone: value });
  };

  // STEP 1 — send OTP
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return showError("Enter a valid email");
    }
    if (!agreed) {
      return showError("You must agree to the Terms and Conditions");
    }
    setLoading(true);
    try {
      const res = await fetch(`${BASE_API}/start-registration`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send OTP");

      // Store email returned by backend
      setEmail(data.email || email);

      showSuccess("OTP sent to your email.");
      setStep(2);
      setResendTimer(60);
      setCanResend(false);
    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // RESEND OTP
  const handleResendOtp = async () => {
    if (!canResend) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE_API}/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to resend OTP");

      showSuccess("OTP resent.");
      setResendTimer(60);
      setCanResend(false);
      setOtp(new Array(6).fill(""));
      if (otpInputs.current?.[0]) {
        otpInputs.current[0].focus();
      }
    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // STEP 2 — verify OTP automatically
  const verifyOtpAuto = useCallback(
    async (fullOtp) => {
      if (loading) return;
      setLoading(true);
      try {
        const res = await fetch(`${BASE_API}/verify-otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, otp: fullOtp }),
        });
        const data = await res.json();

        // ONLY proceed if backend returns a valid pendingRegistrationId
        if (!res.ok || !data.pendingRegistrationId) {
          throw new Error(data.error || "OTP is incorrect");
        }

        setPendingRegistrationId(data.pendingRegistrationId);
        showSuccess("OTP verified successfully!");
        setStep(3);
      } catch (err) {
        showError(err.message);
        setOtp(new Array(6).fill(""));
        if (otpInputs.current?.[0]) {
          otpInputs.current[0].focus();
        }
      } finally {
        setLoading(false);
      }
    },
    [email, loading],
  );

  const handlePaste = (e) => {
    const text = e.clipboardData.getData("text").trim();

    if (!/^\d{6}$/.test(text)) return;

    e.preventDefault();

    const digits = text.split("");
    setOtp(digits);
    verifyOtpAuto(text);

    if (otpInputs.current?.[5]) {
      otpInputs.current[5].focus();
    }
  };

  const handleOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) otpInputs.current[index + 1].focus();
    if (!value && index > 0) otpInputs.current[index - 1].focus();

    if (newOtp.every((d) => d !== "") && !loading) {
      verifyOtpAuto(newOtp.join(""));
    }
  };

  // STEP 3 — complete registration
  const handleCompleteSubmit = async (e) => {
    e.preventDefault();
    if (!pendingRegistrationId) {
      showError("Cannot complete registration without OTP verification.");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      return showError("Passwords do not match");
    }

    if (strength !== "strong") {
      return showError("Password is too weak. Please use a strong password.");
    }
    setLoading(true);
    try {
      const res = await fetch(`${BASE_API}/complete-registration`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pendingRegistrationId,
          fullName: formData.fullName,
          phone: formData.phone,
          password: formData.password,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Registration failed.");

      showSuccess("Registration successful!");
      setTimeout(() => navigate("/login"), 1000);
    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const passwordResult = validatePassword(formData.password, {
    email,
    name: formData.fullName,
  });

  const { checks, score, strength } = passwordResult;

  const strengthMeta = (() => {
    if (score <= 3) return { label: "Weak", color: "danger", width: "25%" };
    if (score <= 6) return { label: "Medium", color: "warning", width: "60%" };
    return { label: "Strong", color: "success", width: "100%" };
  })();

  return (
    <div
      className={darkMode ? "bg-dark text-light" : "bg-light text-dark"}
      style={{ minHeight: "100vh" }}
    >
      <div
        className="container d-flex justify-content-center align-items-center"
        style={{ minHeight: "90vh" }}
      >
        <div
          className={`card p-4 shadow-lg ${
            darkMode ? "bg-secondary text-light" : "bg-white text-dark"
          }`}
          style={{ maxWidth: "420px", width: "100%", borderRadius: "12px" }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="fw-bold">Afro Registration</h3>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => setDarkMode(!darkMode)}
            >
              {darkMode ? "Light Mode" : "Dark Mode"}
            </button>
          </div>

          {/* Progress */}
          <div className="mb-3 text-center">
            <small>Step {step} of 3</small>
            <div className="progress mt-1" style={{ height: "6px" }}>
              <div
                className="progress-bar"
                style={{ width: `${(step / 3) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Messages */}
          {message && (
            <div
              className={`alert alert-${
                messageType === "error" ? "danger" : "success"
              }`}
            >
              {message}
            </div>
          )}

          {/* STEP 1 */}
          {step === 1 && (
            <form onSubmit={handleEmailSubmit}>
              <label>
                Email <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                className="form-control mb-3"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
              <div className="form-check mb-3">
                <input
                  type="checkbox"
                  id="terms"
                  className="form-check-input"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  disabled={loading}
                  required
                />
                <label htmlFor="terms" className="form-check-label">
                  I agree to the{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">
                    Terms and Conditions
                  </a>
                  <span className="text-danger">*</span>
                </label>
              </div>

              <button className="btn btn-primary w-100" disabled={loading}>
                {loading ? "Loading..." : "Next"}
              </button>
            </form>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <>
              <div className="text-center otp-container" onPaste={handlePaste}>
                <label className="mb-2">Enter OTP *</label>

                <div className="d-flex justify-content-between mb-3">
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      type="text"
                      inputMode="numeric"
                      maxLength="1"
                      className="form-control text-center mx-1"
                      style={{ width: "50px", fontSize: "1.5rem" }}
                      value={digit}
                      ref={(el) => (otpInputs.current[i] = el)}
                      onChange={(e) => handleOtpChange(e.target.value, i)}
                      onKeyDown={(e) => {
                        // Handle backspace UX properly
                        if (e.key === "Backspace" && !otp[i] && i > 0) {
                          otpInputs.current[i - 1]?.focus();
                        }
                      }}
                      autoFocus={i === 0}
                      disabled={loading}
                    />
                  ))}
                </div>
              </div>

              <div className="text-center mt-2">
                {canResend ? (
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={handleResendOtp}
                  >
                    Resend OTP
                  </button>
                ) : (
                  <small>Resend OTP in {resendTimer}s</small>
                )}
              </div>
            </>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <form onSubmit={handleCompleteSubmit}>
              <label>
                Full Name <span className="text-danger">*</span>
              </label>
              <input
                className="form-control mb-3"
                value={formData.fullName}
                onChange={(e) =>
                  setFormData({ ...formData, fullName: e.target.value })
                }
                required
              />

              <label>Phone (Ethiopia)</label>
              <input
                className="form-control mb-3"
                value={formData.phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                placeholder="+251"
              />

              <label>
                Password <span className="text-danger">*</span>
              </label>
              <div className="input-group mb-2">
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-control"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>

              {/* Strength bar */}
              {formData.password && (
                <>
                  <div className="progress mb-2" style={{ height: "8px" }}>
                    <div
                      className={`progress-bar bg-${strengthMeta.color}`}
                      style={{ width: strengthMeta.width }}
                    />
                  </div>

                  <small className={`text-${strengthMeta.color}`}>
                    {strengthMeta.label} password
                  </small>

                  <ul className="small mt-2 list-unstyled">
                    <li
                      className={checks.length ? "text-success" : "text-danger"}
                    >
                      {checks.length ? "✔" : "✖"} 6+ characters
                    </li>
                    <li
                      className={
                        checks.uppercase ? "text-success" : "text-danger"
                      }
                    >
                      ✔ Uppercase letter
                    </li>
                    <li
                      className={
                        checks.lowercase ? "text-success" : "text-danger"
                      }
                    >
                      ✔ Lowercase letter
                    </li>
                    <li
                      className={checks.number ? "text-success" : "text-danger"}
                    >
                      ✔ Number
                    </li>
                    <li
                      className={
                        checks.special ? "text-success" : "text-danger"
                      }
                    >
                      ✔ Special character
                    </li>
                    <li
                      className={
                        checks.notCommon ? "text-success" : "text-danger"
                      }
                    >
                      ✔ Not common password
                    </li>
                  </ul>
                </>
              )}

              <label>
                Confirm Password <span className="text-danger">*</span>
              </label>
              <input
                type="password"
                className="form-control mb-3"
                value={formData.confirmPassword}
                onChange={(e) =>
                  setFormData({ ...formData, confirmPassword: e.target.value })
                }
                required
              />

              <button className="btn btn-primary w-100" disabled={loading}>
                {loading ? "Registering..." : "Complete Registration"}
              </button>
            </form>
          )}
          {/* LOGIN LINK */}
          <div className="text-center mt-3">
            <span>Already have an account? </span>
            <button
              className="btn btn-link p-0"
              onClick={() => navigate("/login")}
            >
              Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
